/**
 * Maturity Banding Utility - Assessment Scoring and Classification
 *
 * Implements the simplified maturity scoring system for the maturity-only platform.
 * Calculates operational and strategic maturity bands from assessment responses.
 *
 * Scoring System:
 * - Operational Excellence: 6 questions (score range 6-30)
 * - Strategic Alignment: 5 questions (score range 5-25)
 *
 * Maturity Bands:
 * - Manual: Basic, mostly manual processes
 * - Siloed: Some automation but isolated systems
 * - Managed: Coordinated processes with oversight
 * - Controlled: Systematic controls and monitoring
 * - Autonomous: Fully automated, self-optimizing
 */
import {MaturityResults} from "@shared/interfaces/maturityResultsInterface.ts";


// Strategic Band Mapping
const STRATEGIC_BANDS = {
  'Manual': { min: 5, max: 7 },
  'Siloed': { min: 8, max: 12 },
  'Managed': { min: 13, max: 17 },
  'Controlled': { min: 18, max: 21 },
  'Autonomous': { min: 22, max: 25 }
};

// Operational Band Mapping
const OPERATIONAL_BANDS = {
  'Manual': { min: 6, max: 9 },
  'Siloed': { min: 10, max: 14 },
  'Managed': { min: 15, max: 19 },
  'Controlled': { min: 20, max: 24 },
  'Autonomous': { min: 25, max: 30 }
};

/**
 * Calculate strategic band from score
 */
export function calculateStrategicBand(score: number): string {
  for (const [band, range] of Object.entries(STRATEGIC_BANDS)) {
    if (score >= range.min && score <= range.max) {
      return band;
    }
  }
  return 'Manual'; // Default fallback
}

/**
 * Calculate the operational band from score
 */
export function calculateOperationalBand(score: number): string {
  for (const [band, range] of Object.entries(OPERATIONAL_BANDS)) {
    if (score >= range.min && score <= range.max) {
      return band;
    }
  }
  return 'Manual'; // Default fallback
}

/**
 * Generate maturity results from scores
 */
export function generateMaturityResults(operationalScore: number, strategicScore: number): MaturityResults {
  return {
    operationalScore,
    strategicScore,
    operationalBand: calculateOperationalBand(operationalScore),
    strategicBand: calculateStrategicBand(strategicScore)
  };
}

/**
 * Calculate overall maturity level based on both scores
 */
export function calculateOverallMaturityLevel(maturityResults: MaturityResults): string {
  const { operationalBand, strategicBand } = maturityResults;
  
  // Simple matrix for overall level
  const bandToLevel = {
    'Manual': 1,
    'Siloed': 2,
    'Managed': 3,
    'Controlled': 4,
    'Autonomous': 5
  };
  
  const operationalLevel = bandToLevel[operationalBand as keyof typeof bandToLevel] || 1;
  const strategicLevel = bandToLevel[strategicBand as keyof typeof bandToLevel] || 1;
  
  // Average the two dimensions
  const averageLevel = Math.round((operationalLevel + strategicLevel) / 2);
  
  const levelToBand = {
    1: 'Manual',
    2: 'Siloed',
    3: 'Managed',
    4: 'Controlled',
    5: 'Autonomous'
  };
  
  return levelToBand[averageLevel as keyof typeof levelToBand] || 'Manual';
}

/**
 * Get band color for UI display
 */
export function getBandColor(band: string): string {
  const colors = {
    'Manual': 'bg-red-100 text-red-800',
    'Siloed': 'bg-orange-100 text-orange-800',
    'Managed': 'bg-yellow-100 text-yellow-800',
    'Controlled': 'bg-blue-100 text-blue-800',
    'Autonomous': 'bg-green-100 text-green-800'
  };
  
  return colors[band as keyof typeof colors] || 'bg-gray-100 text-gray-800';
}

/**
 * Get band description for UI display
 */
export function getBandDescription(band: string): string {
  const descriptions = {
    'Manual': 'Basic processes with limited automation',
    'Siloed': 'Departmental tools with minimal integration',
    'Managed': 'Coordinated processes with selective automation',
    'Controlled': 'Orchestrated workflow with real-time visibility',
    'Autonomous': 'Fully automated with predictive capabilities'
  };
  
  return descriptions[band as keyof typeof descriptions] || 'Basic maturity level';
}